import DOMPurify from "dompurify";

/**
 * sanitizeReportHtml cleans the backend-generated Daily Report HTML summary
 * before it's rendered via dangerouslySetInnerHTML.
 *
 * Defense in depth, not a fix for a live exploit: internal/report/formatter.go
 * consistently calls html.EscapeString() on every dynamic value it interpolates
 * (rule names, IPs, target values, sensor/zone names) before building the HTML
 * string, so this path is not currently exploitable (confirmed by inspection,
 * code review 2026-08-24). But that pattern has no frontend safety net -- one
 * future call site in the report generator that forgets to escape a value
 * becomes stored XSS with nothing here to catch it. Sanitizing on the way in
 * means a backend regression degrades to "a tag gets stripped" instead of
 * "a script executes in an operator's authenticated session."
 *
 * The report summary is fully backend-rendered markup (headings, tables,
 * inline styles for its dark preview theme -- see the report package), so the
 * allowlist below is deliberately generous on structure/formatting while still
 * excluding anything that executes script (script tags, event handler
 * attributes, javascript: URLs -- all stripped by DOMPurify regardless of an
 * ALLOWED_TAGS/ATTR list, since it parses and rebuilds the DOM rather than
 * pattern-matching the string).
 */
export function sanitizeReportHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "b", "i", "u", "strong", "em", "br", "hr", "p", "div", "span",
      "h1", "h2", "h3", "h4", "h5", "h6",
      "table", "thead", "tbody", "tr", "th", "td",
      "ul", "ol", "li", "a", "code", "pre",
    ],
    ALLOWED_ATTR: ["style", "href", "target", "rel", "class", "colspan", "rowspan"],
  });
}
